/*
 * Copyright (c) 2000-2023 TeamDev. All rights reserved.
 * TeamDev PROPRIETARY and CONFIDENTIAL.
 * Use is subject to license terms.
 */

import {HttpClient, TypedMessage} from 'spine-web';

/**
 * Provides a security token string.
 *
 * Returns `null` if token is unavailable.
 */
type TokenProvider = () => Promise<string|null>;

/**
 * The implementation of {@link HttpClient} that adds a security token, if any, to all requests
 * made by the client.
 */
export default class AuthorizingHttpClient extends HttpClient {

  /**
   * An application base URL.
   *
   * Defined by the {@link HttpClient}, which is written in JS.
   */
  private readonly _appBaseUrl: string;

  /**
   * Creates a new client instance.
   *
   * @param {string} appBaseUrl              an application base URL
   * @param {TokenProvider} getSecurityToken provides a security token string
   */
  constructor(appBaseUrl, private readonly getSecurityToken: TokenProvider) {
    super(appBaseUrl);
  }

  /**
   * Sends the given message to the given endpoint.
   *
   * The message is sent as in form of a Base64-encoded byte string.
   *
   * @param {!string} endpoint      an endpoint to send the message to
   * @param {!TypedMessage} message a typed message to send
   * @return {Promise<Response|Error>} a message sending promise to be fulfilled with a response,
   *                                   or rejected if an error occurs
   */
  postMessage(endpoint, message) {
    const messageString = message.toBase64();
    const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const url = this._appBaseUrl + path;
    const headers = {
      'Content-Type': 'application/x-protobuf'
    };

    return this.getSecurityToken()
               .then(securityToken => {
                 if (securityToken) {
                   headers['Authorization'] = `Bearer ${securityToken}`;
                 }

                 const request = {
                   method: 'POST',
                   body: messageString,
                   headers: headers,
                   mode: 'cors' as RequestMode,
                 };

                 return fetch(url, request);
               });
  }
}
